import React, {useEffect} from 'react';
import {motion, useMotionValue, useTransform} from 'framer-motion';
import styles from '@dazn/lp-web/src/components/BannerSlider/MobileSlider/mobile-slider.module.css';
import {IMobileSliderCardProps} from '@dazn/lp-web/src/components/BannerSlider/MobileSlider/mobile-slider.types';
import {
    DRAG_CONSTRAINTS,
    DRAG_ELASTIC,
    DRAG_TRANSITION,
    OFFSET_BOUNDARY
} from '@dazn/lp-web/src/components/BannerSlider/MobileSlider/mobile-slider.utils';
import Markdown from 'markdown-to-jsx';
import {getHiddenFields} from '../banner-slider.utils';
import {useLandingPagesContext} from '@dazn/lp-web/src/contexts/LandingPageContext';
import {getButtonItemId} from '@dazn/lp-web/src/proxies';
import {triggerElementClick} from '@dazn/lp-web/src/analytics/fe-events';
import {elementClickEventTypes} from '@dazn/lp-web/src/analytics/fe-events/elementClick';
import {checkIsTravelPassEnabled} from '@dazn/lp-web/src/features/getIsTravelPassEnable';
import {useCountdown} from '@dazn/lp-web/src/hooks/useCountdown';
import {CountdownTimer} from '@dazn/lp-web/src/components/Countdown';
import VideoBanner from '@dazn/lp-web/src/components/VideoBanner/VideoBanner';
import {applyGoldColor} from '../../ContentTiers/ContentTiers';
import isQueryParamTrue from '@dazn/lp-web/src/utils/isQueryparamTrue';
import classNames from 'classnames';
import {useVideoBannerContext} from '../../VideoBanner/VideoBannerContext';
import {bannerTypes} from '@dazn/lp-web/src/constants/bannerTypes.constants';
import {MobileRotatingBannerContent} from './MobileRotatingBannerContent';
import {BadgeTypes, PRICE_VARIABLE} from '@dazn/lp-web/src/types';

const MobileSliderCard: React.FC<IMobileSliderCardProps> = (props) => {
    const {
        data,
        isDraggable,
        onTouchStart,
        onTouchEnd,
        onSwipe,
        activeIndex,
        rotatingBannerIndex
    } = props;
    const {getPromoCodeByEntSetId} = useLandingPagesContext();
    const [isColumnType, setIsColumnType] = React.useState(false);
    const promoCode = getPromoCodeByEntSetId(data?.entitlementSetId);
    const x = useMotionValue(0);
    const inputX = [50 * -1, 0, 50];
    const inputRotate = [100 * -1, 0, 100];
    const outputX = [-200, 0, 200];
    const outputRotate = [-15, 0, 15];
    const drivenX = useTransform(x, inputX, outputX);
    const drivenRotation = useTransform(x, inputRotate, outputRotate);
    const videoPosterImage = data?.videoPosterImage;
    const videoUrl = data?.videoUrl;

    const isVideoBanner = data?.backgroundType === 'video';
    const {setThumbnail} = useVideoBannerContext();

    const {hideDescription, hideLogo, hideTitle, hidePrice} =
        getHiddenFields(data.hideFieldsInMobile) ?? {};

    const timer = Boolean(data?.showCountDownTimer)
        ? useCountdown(data?.countdownTime)
        : null;

    const handleDragEnd = (_: any, info: any) => {
        const isOffRightBoundary = info.offset.x > OFFSET_BOUNDARY;
        const isOffLeftBoundary = info.offset.x < -OFFSET_BOUNDARY;
        const isOffBoundary = isOffRightBoundary || isOffLeftBoundary;
        const direction = isOffRightBoundary ? 'right' : 'left';
        const noVirtualDrag = info.offset.y >= -100;

        if (isOffBoundary && noVirtualDrag) {
            onSwipe({
                id: data?.id,
                direction: direction,
                endTransform: {
                    x: drivenX.get(),
                    rotate: drivenRotation.get()
                }
            });
        }
    };

    useEffect(() => {
        setThumbnail(videoPosterImage);
    }, [isVideoBanner]);

    useEffect(() => {
        if (
            data?.actions?.some(
                (btn: any) => btn?.mobileButtonLabel?.length > 12
            ) &&
            data?.actions?.length > 1
        ) {
            setIsColumnType(true);
        }
    }, [data?.actions]);
    const isRotatingContentBanner =
        data?.bannerType?.toLowerCase() ===
            bannerTypes.ROTATING_CONTENT.toLowerCase() &&
        activeIndex === rotatingBannerIndex;

    const hidePriceString =
        typeof data?.price === 'string' &&
        data?.price?.includes(PRICE_VARIABLE);

    return (
        <>
            <motion.div
                className={classNames(styles.bannerCardInner, {
                    [styles.bannerCardInnerForRotatingBanner]:
                        isRotatingContentBanner || rotatingBannerIndex === 0
                })}
                style={{rotate: drivenRotation, x: drivenX}}
            >
                {data?.backgroundType === 'image' && (
                    <picture>
                        {data?.backgroundImage?.mobile && (
                            <source
                                media={`(max-width: 767px)`}
                                srcSet={data?.backgroundImage.mobile}
                            />
                        )}
                        {data?.backgroundImage?.default && (
                            <img
                                data-target="banner-image"
                                className={styles.bannerImage}
                                src={data?.backgroundImage.default}
                            />
                        )}
                    </picture>
                )}
                {data?.backgroundType === 'video' && (
                    <>
                        <picture>
                            {videoPosterImage?.mobile && (
                                <source
                                    media={`(max-width: 767px)`}
                                    srcSet={videoPosterImage?.mobile}
                                />
                            )}
                            {videoPosterImage?.default && (
                                <img
                                    data-target="banner-image"
                                    className={styles.bannerImage}
                                    src={videoPosterImage?.default}
                                />
                            )}
                        </picture>
                        <VideoBanner title={data?.title} url={videoUrl} />
                    </>
                )}
                <div
                    className={classNames(styles.bannerContent, {
                        [styles.bannerContentRotatingBanner]:
                            isRotatingContentBanner
                    })}
                >
                    {data?.goldenLabel && (
                        <div className={styles.bannerLabelWrapper}>
                            <p
                                className={classNames(
                                    styles.bannerLabel,
                                    styles.bannerLabelGolden,
                                    {
                                        [styles.rotatingGoldLabel]:
                                            isRotatingContentBanner
                                    }
                                )}
                                data-target="banner-label"
                            >
                                {data?.goldenLabel}
                            </p>
                        </div>
                    )}
                    {data?.logo && !hideLogo && (
                        <img
                            className={styles.bannerLogo}
                            src={data?.logo}
                            data-target="banner-logo"
                        />
                    )}
                    {data?.label && (
                        <div className={styles.bannerLabelWrapper}>
                            <p
                                className={classNames(styles.bannerLabel, {
                                    [styles.bannerLabelGolden]:
                                        data?.lableType === BadgeTypes.GOLDEN,
                                    [styles.bannerLabelSecondary]:
                                        data?.lableType ===
                                        BadgeTypes.SECONDARY,
                                    [styles.rotatingGoldLabel]:
                                        isRotatingContentBanner
                                })}
                                data-target="banner-label"
                            >
                                {data?.label}
                            </p>
                        </div>
                    )}
                    {Boolean(data?.showCountDownTimer) &&
                        data?.countdownVariant === 'V2' && (
                            <div
                                style={{paddingBlock: '12px'}}
                                data-target="banner-countdown"
                            >
                                <CountdownTimer
                                    hideCountdownZero={data?.hideCountdownZero}
                                    timer={timer}
                                    units={data?.countdownUnits}
                                    variant="V2"
                                />
                            </div>
                        )}
                    {data?.description &&
                        !hideDescription &&
                        isRotatingContentBanner && (
                            <Markdown
                                className={styles.rotatingBannerDescription}
                                data-target="banner-description"
                                options={{
                                    forceBlock: true
                                }}
                            >
                                {applyGoldColor(data?.description)}
                            </Markdown>
                        )}
                    {isRotatingContentBanner && data?.title && !hideTitle && (
                        <Markdown
                            as="p"
                            className={styles.rotatingBannerTitle}
                            data-target="banner-title"
                            options={{
                                forceBlock: true
                            }}
                        >
                            {data?.title}
                        </Markdown>
                    )}

                    {data?.title &&
                        !isRotatingContentBanner &&
                        !hideTitle &&
                        (checkIsTravelPassEnabled() ? (
                            <Markdown
                                className={styles.bannerTitleTravelPass}
                                data-target="banner-title"
                                options={{
                                    forceBlock: true
                                }}
                            >
                                {applyGoldColor(data?.title)}
                            </Markdown>
                        ) : (
                            <Markdown
                                className={styles.bannerTitle}
                                data-target="banner-title"
                                options={{
                                    forceBlock: true
                                }}
                            >
                                {applyGoldColor(data?.title)}
                            </Markdown>
                        ))}

                    {data?.description &&
                        !hideDescription &&
                        !isRotatingContentBanner && (
                            <Markdown
                                className={styles.bannerDescription}
                                data-target="banner-description"
                                options={{
                                    forceBlock: true
                                }}
                            >
                                {applyGoldColor(data?.description)}
                            </Markdown>
                        )}

                    {isRotatingContentBanner &&
                    data?.rotatingContentItem?.length > 0 ? (
                        <MobileRotatingBannerContent
                            rotatingContentItem={
                                data?.rotatingContentItem || []
                            }
                        />
                    ) : null}
                    {data?.showPrice &&
                        data?.price &&
                        !hidePrice &&
                        !hidePriceString && (
                            <p
                                className={styles.price}
                                data-target="banner-price"
                            >
                                {data?.price}
                            </p>
                        )}

                    {data?.offerLabel && data?.showOfferLabel && (
                        <p
                            className={styles.bannerOfferLabel}
                            data-target="banner-offer-label"
                        >
                            {data?.offerLabel}
                        </p>
                    )}
                    {Boolean(data?.showCountDownTimer) &&
                        data?.countdownVariant === 'V1' && (
                            <div
                                style={{paddingBlock: '2px 16px'}}
                                data-target="banner-countdown"
                            >
                                <CountdownTimer
                                    hideCountdownZero={data?.hideCountdownZero}
                                    timer={timer}
                                    units={data?.countdownUnits}
                                    variant="V1"
                                />
                            </div>
                        )}
                    {Boolean(data?.actions?.length) && (
                        <div
                            className={classNames(styles.bannerActions, {
                                [styles.columnFlex]: isColumnType,
                                [styles.rotatingBannerButtonAction]:
                                    isRotatingContentBanner
                            })}
                            data-target="banner-actions"
                        >
                            {data?.actions?.map((btn, idx: number) => {
                                return isQueryParamTrue('seo') ? (
                                    <a
                                        key={idx}
                                        href={btn.navigationLink}
                                        className={classNames(
                                            'seoButton',
                                            styles.seoMobileBannerButton,
                                            {
                                                [styles.golden]:
                                                    btn.buttonType === 'golden',
                                                [styles.primary]:
                                                    btn.buttonType ===
                                                        'primary' ||
                                                    !btn.buttonType
                                            }
                                        )}
                                    >
                                        {btn?.mobileButtonLabel}
                                    </a>
                                ) : (
                                    <button
                                        key={idx}
                                        onClick={() => {
                                            const buttonItemId =
                                                getButtonItemId(btn);
                                            triggerElementClick({
                                                section:
                                                    elementClickEventTypes.BODY,
                                                itemId: buttonItemId,
                                                resourceString:
                                                    btn.mobileButtonLabel ||
                                                    btn.label
                                            });
                                            btn.action({promoCode});
                                        }}
                                        data-index={idx.toString()}
                                        className={
                                            btn.buttonType === 'golden'
                                                ? styles.golden
                                                : btn.buttonType ===
                                                      'primary' ||
                                                  !btn.buttonType
                                                ? styles.primary
                                                : ''
                                        }
                                        dangerouslySetInnerHTML={{
                                            __html: btn?.mobileButtonLabel ?? ''
                                        }}
                                    ></button>
                                );
                            })}
                        </div>
                    )}
                    {data?.disclaimer && (
                        <Markdown
                            className={classNames(styles.bannerDisclaimer, {
                                [styles.bannerDisclaimerRotatingBanner]:
                                    isRotatingContentBanner
                            })}
                            data-target="banner-disclaimer"
                            options={{
                                forceBlock: true
                            }}
                        >
                            {data?.disclaimer}
                        </Markdown>
                    )}
                </div>
            </motion.div>
            <motion.div
                drag="x"
                dragSnapToOrigin
                dragElastic={DRAG_ELASTIC}
                dragConstraints={DRAG_CONSTRAINTS}
                dragTransition={DRAG_TRANSITION}
                onDragEnd={handleDragEnd}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                className={styles.bannerCardInnerOverlay}
                style={{x, pointerEvents: isDraggable ? 'auto' : 'none'}}
            ></motion.div>
        </>
    );
};

export default MobileSliderCard;
